import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { useHistory, useLocation } from 'react-router'
import { ColorRing } from 'react-loader-spinner'

import '../Make/Make.css'
import MakeHeader from '../ReusableComponents/Header/MakeHeader'
import getBackendImage from '../../source/imageUtil'
import { uploadImage } from '../../source/imageUtil'

const BACKEND_URL = process.env.REACT_APP_API_BASE

const MainPlaydynamexAdmin = () => {
    const history = useHistory()
    const location = useLocation()
    const [projects, setProjects] = useState([])
    const [loading, setLoading] = useState(true)

    const [ttsApiKey, setTtsApiKey] = useState(
        localStorage.getItem('freepikApiKey') || ''
    )
    const [ttsVoiceId, setTtsVoiceId] = useState(
        localStorage.getItem('elevenVoiceId') || ''
    )

    const [user, setUser] = useState(null)
    const [authLoading, setAuthLoading] = useState(true)
    const [loginError, setLoginError] = useState('')
    const [isLoggingIn, setIsLoggingIn] = useState(false)

    useEffect(() => {
        const params = new URLSearchParams(location.search)
        const errorParam = params.get('error')

        if (errorParam) {
            setLoginError(
                errorParam === 'unauthorized'
                    ? 'Login authentication failed: Account not authorized.'
                    : 'Login authentication failed. Please try again.'
            )
            history.replace('/playdynamex-main-admin') // Update to whatever route this mounts on
        }

        fetch(`${BACKEND_URL}/me`, { credentials: 'include' })
            .then((res) => res.json())
            .then((data) => {
                setUser(data)
                setAuthLoading(false)
            })
            .catch(() => {
                setUser(null)
                setAuthLoading(false)
            })
    }, [location.search, history])

    useEffect(() => {
        if (user) fetchProjects()
    }, [user])

    const fetchProjects = () => {
        setLoading(true)
        fetch(`${BACKEND_URL}/api/staging/playdynamex-standalone`, {
            credentials: 'include',
        })
            .then((res) => res.json())
            .then((data) => {
                setProjects(data.projects || [])
                setLoading(false)
            })
            .catch((err) => {
                console.error('Failed to load staging projects:', err)
                setLoading(false)
            })
    }

    const handleEditGrade = async (e, projectId, newGrade) => {
        e.stopPropagation()
        try {
            // Fetch existing staging configuration data
            const res = await fetch(
                `${BACKEND_URL}/api/staging/playdynamex-standalone/${projectId}`,
                { credentials: 'include' }
            )
            const data = await res.json()

            // Mutate the grade value
            data.grade = newGrade

            // Push updates to staging database
            await fetch(
                `${BACKEND_URL}/api/staging/playdynamex-standalone/${projectId}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify(data),
                }
            )

            // Update local state grid array instantly
            setProjects(
                projects.map((p) =>
                    p.id === projectId ? { ...p, grade: newGrade } : p
                )
            )
        } catch (err) {
            console.error('Failed to change project grade alignment:', err)
            alert('Could not update project grade.')
        }
    }

    const handleSaveTtsConfig = () => {
        localStorage.setItem('freepikApiKey', ttsApiKey.trim())
        localStorage.setItem('elevenVoiceId', ttsVoiceId.trim())
        alert('TTS Settings saved locally to your browser!')
    }

    const handlePublish = async () => {
        // Step 1: Check missing images
        const missingImages = projects.filter(
            (p) => !p.image || p.image === 'projectDefault'
        )
        if (missingImages && missingImages.length > 0) {
            const names = missingImages.map((p) => p.title).join(', ')
            window.alert(
                `Cannot publish! The following projects are missing a custom image: ${names}.\n\nPlease set an image for each by clicking "Change Image".`
            )
            return
        }

        const confirmPublish = window.confirm(
            'Are you sure you want to upload all the data? This is permanent and will overwrite live data for regular users.'
        )
        if (!confirmPublish) return

        setLoading(true)
        try {
            // Dedicated route: publishes ONLY standalone Playdynamex data.
            const response = await fetch(
                `${BACKEND_URL}/api/publish-playdynamex-standalone`,
                {
                    method: 'POST',
                    credentials: 'include',
                }
            )

            if (response.ok) alert('Success! All Playdynamex data is now live.')
            else alert('Error: Failed to publish data.')
        } catch (err) {
            console.error('Publish failed:', err)
            alert('A network error occurred while publishing.')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async (e, projectId) => {
        e.stopPropagation()
        if (
            !window.confirm(
                'Are you sure you want to delete this project? This is irreversible.'
            )
        )
            return

        try {
            await fetch(
                `${BACKEND_URL}/api/staging/playdynamex-standalone/${projectId}`,
                {
                    method: 'DELETE',
                    credentials: 'include',
                }
            )
            setProjects(projects.filter((p) => p.id !== projectId))
        } catch (err) {
            console.error('Failed to delete project:', err)
        }
    }

    const handleEditTitle = async (e, projectId) => {
        e.stopPropagation()
        const project = projects.find((p) => p.id === projectId)
        const newTitle = window.prompt(
            'Enter new title for project:',
            project.title
        )

        const MAX_TITLE_LENGTH = 42

        if (newTitle && newTitle.trim() !== '' && newTitle !== project.title) {
            if (newTitle.trim().length > MAX_TITLE_LENGTH) {
                alert(`Title must be ${MAX_TITLE_LENGTH} characters or less.`)
                return
            }

            try {
                // Fetch existing content so we don't wipe it out, just modify title
                const res = await fetch(
                    `${BACKEND_URL}/api/staging/playdynamex-standalone/${projectId}`,
                    { credentials: 'include' }
                )
                const data = await res.json()
                data.title = newTitle.trim()

                await fetch(
                    `${BACKEND_URL}/api/staging/playdynamex-standalone/${projectId}`,
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify(data),
                    }
                )

                setProjects(
                    projects.map((p) =>
                        p.id === projectId
                            ? { ...p, title: newTitle.trim() }
                            : p
                    )
                )
            } catch (err) {
                console.error('Failed to rename project', err)
                alert('Could not rename project.')
            }
        }
    }

    const handleCreate = async () => {
        const MAX_TITLE_LENGTH = 60
        const title = window.prompt('Enter title for the new project:')
        if (title && title.trim() !== '') {
            if (title.trim().length > MAX_TITLE_LENGTH) {
                alert(`Title must be ${MAX_TITLE_LENGTH} characters or less.`)
                return
            }
            try {
                const res = await fetch(
                    `${BACKEND_URL}/api/staging/playdynamex-standalone`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ title: title.trim() }),
                    }
                )
                const newProject = await res.json()

                if (newProject.success) {
                    setProjects([
                        ...projects,
                        {
                            id: newProject.projectId,
                            title: newProject.title,
                            image: 'projectDefault',
                        },
                    ])
                }
            } catch (err) {
                console.error('Failed to create project:', err)
                alert('Could not register project on backend.')
            }
        }
    }

    const handleImageUpload = async (e, projectId) => {
        e.stopPropagation()
        const file = e.target.files[0]
        if (!file) return

        if (
            file.type !== 'image/png' &&
            !file.name.toLowerCase().endsWith('.png')
        ) {
            alert('Please select a valid .png image.')
            e.target.value = ''
            return
        }

        if (file.size > 10 * 1024 * 1024) {
            alert('Image is too large! Maximum allowed size is 10MB.')
            e.target.value = ''
            return
        }

        try {
            const filename = await uploadImage(file)

            // Fetch current data, update image tag, save back
            const res = await fetch(
                `${BACKEND_URL}/api/staging/playdynamex-standalone/${projectId}`,
                { credentials: 'include' }
            )
            const data = await res.json()
            data.image = filename

            await fetch(
                `${BACKEND_URL}/api/staging/playdynamex-standalone/${projectId}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify(data),
                }
            )

            setProjects(
                projects.map((p) =>
                    p.id === projectId ? { ...p, image: filename } : p
                )
            )
        } catch (err) {
            console.error('Upload failed', err)
            alert('Image upload failed')
        }
    }

    if (authLoading)
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                }}
            >
                <ColorRing
                    visible={true}
                    height="80"
                    width="80"
                    colors={[
                        '#e15b64',
                        '#f47e60',
                        '#f8b26a',
                        '#abbd81',
                        '#849b87',
                    ]}
                />
            </div>
        )

    if (!user)
        return (
            // Reusing your login template
            <div className="Main_Select">
                <div className="Select_Headers">
                    <MakeHeader
                        title="Admin Access"
                        titlePosition="left"
                        isGoBack={true}
                        goBack={() => history.push('/playdynamex-main')}
                        showAccountSetting={false}
                        showBluetoothBtn={false}
                    />
                </div>
                <div
                    className="TokiKits_Body"
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '60vh',
                    }}
                >
                    <div
                        style={{
                            textAlign: 'center',
                            maxWidth: '380px',
                            width: '100%',
                            backgroundColor: 'white',
                            padding: '40px 30px',
                            borderRadius: '16px',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                            border: '1px solid #eaeaea',
                        }}
                    >
                        <div style={{ marginBottom: '20px' }}>
                            <svg
                                height="48"
                                viewBox="0 0 16 16"
                                version="1.1"
                                width="48"
                                aria-hidden="true"
                            >
                                <path
                                    fill="#2f2e65"
                                    fillRule="evenodd"
                                    d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"
                                ></path>
                            </svg>
                        </div>
                        <div
                            style={{
                                fontSize: '20px',
                                color: '#2f2e65',
                                marginBottom: '10px',
                                fontFamily: 'Halcyon_SemiBold, sans-serif',
                            }}
                        >
                            Toki Admin Portal
                        </div>
                        <div
                            style={{
                                fontSize: '14px',
                                color: '#666',
                                marginBottom: '20px',
                                lineHeight: '1.5',
                            }}
                        >
                            Sign in with an authorized GitHub account to manage
                            standalone mechanical content.
                        </div>
                        {loginError && (
                            <div
                                style={{
                                    marginBottom: '20px',
                                    padding: '12px',
                                    backgroundColor: '#fff2f2',
                                    color: '#d32f2f',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    border: '1px solid #ffcdd2',
                                }}
                            >
                                {loginError}
                            </div>
                        )}
                        <button
                            onClick={() => {
                                setIsLoggingIn(true)
                                // Pass the target route in the state query parameter
                                window.location.href = `${BACKEND_URL}/auth/github?state=/playdynamex-main-admin`
                            }}
                            disabled={isLoggingIn}
                            style={{
                                width: '100%',
                                padding: '12px 18px',
                                backgroundColor: isLoggingIn
                                    ? '#5c5b8b'
                                    : '#2f2e65',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontWeight: 'bold',
                                cursor: isLoggingIn ? 'not-allowed' : 'pointer',
                            }}
                        >
                            {isLoggingIn
                                ? 'Connecting...'
                                : 'Login with GitHub'}
                        </button>
                    </div>
                </div>
            </div>
        )

    return (
        <div className="Main_Select">
            <div className="Select_Headers">
                <MakeHeader
                    title="Admin: Playdynamex"
                    titlePosition="left"
                    isGoBack={true}
                    goBack={() => history.push('/playdynamex-main')}
                    showAccountSetting={true}
                    showBluetoothBtn={false}
                />
                <button
                    onClick={handlePublish}
                    style={{
                        position: 'absolute',
                        top: '50%',
                        right: '300px',
                        transform: 'translateY(-50%)',
                        backgroundColor: '#ff4b4b',
                        color: 'white',
                        border: 'none',
                        padding: '8px 20px',
                        borderRadius: '20px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        zIndex: 1000,
                    }}
                >
                    Publish to Live
                </button>

                <button
                    onClick={() => history.push('/playdynamex-main')}
                    style={{
                        position: 'absolute',
                        top: '50%',
                        right: '180px',
                        transform: 'translateY(-50%)',
                        padding: '6px 12px',
                        backgroundColor: '#2f2e65',
                        color: 'white',
                        border: 'none',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        zIndex: 1000,
                        boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                    }}
                >
                    Exit Admin
                </button>
            </div>

            <div
                style={{
                    maxWidth: '1200px',
                    padding: '15px 25px',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px',
                    position: 'fixed',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 1000,
                }}
            >
                <strong style={{ color: '#2f2e65', fontSize: '14px' }}>
                    Voiceover Settings:
                </strong>
                <input
                    type="text"
                    placeholder="ElevenLabs API Key"
                    value={ttsApiKey}
                    onChange={(e) => setTtsApiKey(e.target.value)}
                    style={{
                        padding: '8px',
                        borderRadius: '6px',
                        border: '1px solid #ccc',
                        flex: 1,
                        fontSize: '13px',
                    }}
                />
                <input
                    type="text"
                    placeholder="Voice ID"
                    value={ttsVoiceId}
                    onChange={(e) => setTtsVoiceId(e.target.value)}
                    style={{
                        padding: '8px',
                        borderRadius: '6px',
                        border: '1px solid #ccc',
                        fontSize: '13px',
                    }}
                />
                <button
                    onClick={handleSaveTtsConfig}
                    style={{
                        backgroundColor: '#ff9800',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                    }}
                >
                    Save
                </button>
            </div>

            <div className="TokiKits_Body">
                {loading && (
                    <div
                        style={{
                            gridColumn: '1 / -1',
                            textAlign: 'center',
                            padding: '40px',
                        }}
                    >
                        <ColorRing
                            visible={true}
                            height="60"
                            width="60"
                            colors={[
                                '#e15b64',
                                '#f47e60',
                                '#f8b26a',
                                '#abbd81',
                                '#849b87',
                            ]}
                        />
                    </div>
                )}

                {!loading &&
                    projects.map((proj) => (
                        <div
                            key={proj.id}
                            className="TokiKit_Card"
                            onClick={() =>
                                history.push(`/PlaydynamexAdmin/${proj.id}`)
                            }
                            style={{ paddingBottom: '10px' }}
                        >
                            <div
                                className="TokiKit_CardImg"
                                style={{ position: 'relative' }}
                            >
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: '10px',
                                        right: '10px',
                                        zIndex: 5,
                                    }}
                                >
                                    <label
                                        style={{
                                            background: '#007BFF',
                                            color: 'white',
                                            padding: '6px 10px',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            cursor: 'pointer',
                                            fontFamily:
                                                'Halcyon_SemiBold, sans-serif',
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        Change Image
                                        <input
                                            type="file"
                                            accept=".png, image/png"
                                            style={{ display: 'none' }}
                                            onChange={(e) =>
                                                handleImageUpload(e, proj.id)
                                            }
                                        />
                                    </label>
                                </div>
                                <img
                                    src={getBackendImage(
                                        proj.image || 'projectDefault'
                                    )}
                                    alt={proj.title}
                                />
                            </div>
                            <p className="TokiKit_CardName">{proj.title}</p>

                            {/* Streamlined Admin Controls (No Reorder functionality for Standalone) */}
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    gap: '6px',
                                    fontSize: '13px',
                                    width: '100%',
                                    marginTop: 'auto',
                                }}
                            >
                                {/* --- New Grade Dropdown Selector --- */}
                                <select
                                    value={proj.grade || 'Grade 4'}
                                    onClick={(e) => e.stopPropagation()} // Keeps card navigation from firing
                                    onChange={(e) =>
                                        handleEditGrade(
                                            e,
                                            proj.id,
                                            e.target.value
                                        )
                                    }
                                    style={{
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        padding: '2px 4px',
                                        fontSize: '12px',
                                        color: '#2f2e65',
                                        cursor: 'pointer',
                                        backgroundColor: '#fff',
                                    }}
                                >
                                    <option value="Grade 4">Grade 4</option>
                                    <option value="Grade 5">Grade 5</option>
                                    <option value="Grade 6">Grade 6</option>
                                    <option value="Grade 7">Grade 7</option>
                                    <option value="Grade 8">Grade 8</option>
                                </select>

                                <span
                                    style={{ color: '#ccc', margin: '0 4px' }}
                                >
                                    |
                                </span>

                                <button
                                    onClick={(e) => handleEditTitle(e, proj.id)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#007BFF',
                                        cursor: 'pointer',
                                        padding: 0,
                                    }}
                                >
                                    Edit Title
                                </button>
                                <span
                                    style={{ color: '#ccc', margin: '0 8px' }}
                                >
                                    |
                                </span>
                                <button
                                    onClick={(e) => handleDelete(e, proj.id)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#DC3545',
                                        cursor: 'pointer',
                                        padding: 0,
                                    }}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))}

                {!loading && (
                    <div
                        className="TokiKit_Card"
                        onClick={handleCreate}
                        style={{
                            background: 'rgba(255,255,255,0.5)',
                            border: '2px dashed #999',
                            boxShadow: 'none',
                            justifyContent: 'center',
                            minHeight: '34vh',
                            color: '#666',
                            fontWeight: 'bold',
                        }}
                    >
                        + Create new project
                    </div>
                )}
            </div>
        </div>
    )
}

export default connect()(MainPlaydynamexAdmin)
